import type { ComparisonOperators, MatchValue } from '../types/color-rule.js';

export type Range = { from: number; to: number };
type ValueMatcher = Record<
  ComparisonOperators,
  <T extends MatchValue>(subject: T, value: T) => boolean
>;
export type RangeComparisonOperators =
  | 'less-than'
  | 'less-or-equal'
  | 'greater-than'
  | 'greater-or-equal'
  | 'equals'
  | 'not-equals';
type RangeMatcher = Record<
  RangeComparisonOperators,
  (subject: Range, value: number) => boolean
>;

const isSameType = (a: unknown, b: unknown) => {
  return typeof a === typeof b;
};

export const VALUE_MATCHERS: ValueMatcher = {
  equals: (subject, value) => `${subject}` === `${value}`,
  'not-equals': (subject, value) => `${subject}` !== `${value}`,
  'less-than': (subject, value) =>
    isSameType(subject, value) && subject < value,
  'less-or-equal': (subject, value) =>
    isSameType(subject, value) && subject <= value,
  'greater-than': (subject, value) =>
    isSameType(subject, value) && subject > value,
  'greater-or-equal': (subject, value) =>
    isSameType(subject, value) && subject >= value,
  'starts-with': (subject, value) => `${subject}`.startsWith(`${value}`),
  'not-starts-with': (subject, value) => !`${subject}`.startsWith(`${value}`),
  'ends-with': (subject, value) => `${subject}`.endsWith(`${value}`),
  'not-ends-with': (subject, value) => !`${subject}`.endsWith(`${value}`),
  contains: (subject, value) => `${subject}`.includes(`${value}`),
  'not-contains': (subject, value) => !`${subject}`.includes(`${value}`),
  'matches-phrase': (subject, value) => subject === value,
  'not-matches-phrase': (subject, value) => subject !== value,
};

export const RANGE_MATCHERS: RangeMatcher = {
  'less-than': (subject, value) =>
    isSameType(subject.to, value) && subject.to < value,
  'less-or-equal': (subject, value) =>
    isSameType(subject.to, value) && subject.to <= value,
  'greater-than': (subject, value) =>
    isSameType(subject.to, value) && subject.from > value,
  'greater-or-equal': (subject, value) =>
    isSameType(subject.to, value) && subject.from >= value,
  equals: (subject, value) => subject.to === value && subject.from === value,
  'not-equals': (subject, value) =>
    !(subject.to === value && subject.from === value),
};

export const isRangeOperator = (
  operator: string,
): operator is RangeComparisonOperators => {
  return operator in RANGE_MATCHERS;
};

export function buildValidator(
  matchers: ValueMatcher,
): <T extends MatchValue>(
  subject: T,
  comparator: ComparisonOperators,
  value: T,
) => boolean;
export function buildValidator(
  matchers: RangeMatcher,
): (
  subject: Range,
  comparator: RangeComparisonOperators,
  value: number,
) => boolean;
export function buildValidator<
  M extends ValueMatcher | RangeMatcher,
  S extends MatchValue | Range,
  V extends MatchValue | number,
>(matchers: M) {
  return (subject: S, comparator: keyof M, value: V) => {
    if (comparator in matchers) {
      return (matchers[comparator] as (subject: S, value: V) => boolean)(
        subject,
        value,
      );
    }

    return false;
  };
}
